import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ScrollReveal, { ScrollStaggerContainer } from '@/components/base/ScrollReveal';

export default function WhyChooseUs() {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const services = [
    { icon: 'ri-line-chart-line', titleKey: 'services.strategy', descKey: 'services.strategyDesc' },
    { icon: 'ri-meta-line', titleKey: 'services.metaAds', descKey: 'services.metaAdsDesc' },
    { icon: 'ri-settings-3-line', titleKey: 'services.automation', descKey: 'services.automationDesc' },
    { icon: 'ri-robot-line', titleKey: 'services.ai', descKey: 'services.aiDesc' },
    { icon: 'ri-macbook-line', titleKey: 'services.webDesign', descKey: 'services.webDesignDesc' },
    { icon: 'ri-filter-3-line', titleKey: 'services.funnels', descKey: 'services.funnelsDesc' },
    { icon: 'ri-user-add-line', titleKey: 'services.leads', descKey: 'services.leadsDesc' },
  ];

  return (
    <section id="services" className="py-20 md:py-28 bg-gray-50 overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up" duration={0.7}>
          <div className="text-center mb-14 md:mb-18">
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('services.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('services.title')}{' '}
              <span className="text-primary-500">{t('services.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <ScrollStaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" staggerDelay={0.08} delay={0.2}>
          {services.map((service, index) => (
            <motion.div
              key={service.titleKey}
              className={`relative p-6 md:p-7 rounded-2xl transition-all duration-500 cursor-pointer ${
                hoveredIndex === index
                  ? 'bg-primary-500 scale-[1.02] z-10'
                  : 'bg-white hover:bg-gray-50/80'
              }`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              whileHover={{ y: -4 }}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-500 ${
                  hoveredIndex === index
                    ? 'bg-white/20 text-white'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                <i className={`${service.icon} text-xl`}></i>
              </div>
              <h3
                className={`font-heading text-base font-semibold mb-2 transition-colors duration-500 ${
                  hoveredIndex === index ? 'text-white' : 'text-gray-900'
                }`}
              >
                {t(service.titleKey)}
              </h3>
              <p
                className={`text-sm leading-relaxed transition-colors duration-500 ${
                  hoveredIndex === index ? 'text-white/80' : 'text-gray-600'
                }`}
              >
                {t(service.descKey)}
              </p>
              <div
                className={`mt-4 flex items-center gap-1 text-sm font-medium transition-all duration-500 ${
                  hoveredIndex === index ? 'text-white opacity-100' : 'text-red-600 opacity-0'
                }`}
              >
                {t('services.learnMore')} <i className="ri-arrow-right-line"></i>
              </div>
            </motion.div>
          ))}
        </ScrollStaggerContainer>
      </div>
    </section>
  );
}